import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from 'react';
import { notificationApi, AppNotification } from '../services/api';
import { acquireNotificationHub, releaseNotificationHub, shouldLoadNotificationData, markNotificationDataLoaded } from '../services/notificationHub';
import { useAuth } from './AuthContext';

interface NotificationContextType {
  unreadCount: number;
  notifications: AppNotification[];
  totalNotifications: number;
  loading: boolean;
  loadNotifications: (page?: number) => Promise<void>;
  markAsRead: (id: number) => Promise<void>;
  markAsUnread: (id: number) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  deleteNotification: (id: number) => Promise<void>;
}

const NotificationContext = createContext<NotificationContextType>({
  unreadCount: 0,
  notifications: [],
  totalNotifications: 0,
  loading: false,
  loadNotifications: async () => {},
  markAsRead: async () => {},
  markAsUnread: async () => {},
  markAllAsRead: async () => {},
  deleteNotification: async () => {},
});

export const useNotifications = () => useContext(NotificationContext);

const PAGE_SIZE = 20;
/** Defer badge count so dashboard / scheduling critical APIs claim connections first. */
const UNREAD_COUNT_DEFER_MS = 2500;

export const NotificationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { employee, isAuthenticated } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [totalNotifications, setTotalNotifications] = useState(0);
  const [loading, setLoading] = useState(false);

  const loadNotifications = useCallback(async (page = 1) => {
    if (!isAuthenticated) return;
    setLoading(true);
    try {
      const result = await notificationApi.getAll(page, PAGE_SIZE);
      setNotifications(prev => page === 1 ? result.items : [...prev, ...result.items]);
      setTotalNotifications(result.total);
    } catch {
      // silent fail
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  const loadUnreadCount = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const count = await notificationApi.getUnreadCount();
      setUnreadCount(count);
    } catch {
      // silent fail
    }
  }, [isAuthenticated]);

  // Connect to SignalR when authenticated (shared hub survives Strict Mode remounts)
  useEffect(() => {
    if (!isAuthenticated || !employee?.id) return;

    acquireNotificationHub(employee.id, {
      onNotification: (notification: AppNotification) => {
        setNotifications(prev => {
          const exists = prev.some(n => n.id === notification.id);
          if (exists) return prev;
          return [notification, ...prev];
        });
        setTotalNotifications(prev => prev + 1);
      },
      onUnreadCount: (count: number) => {
        setUnreadCount(count);
      },
    });

    let idleId: number | undefined;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    let cancelled = false;

    // Badge count only (list loads when the panel opens). Defer past first paint
    // so overview week APIs are not queued behind notification REST.
    if (shouldLoadNotificationData(employee.id)) {
      const runDeferredUnread = () => {
        if (cancelled) return;
        if (!shouldLoadNotificationData(employee.id)) return;
        markNotificationDataLoaded(employee.id);
        void loadUnreadCount();
      };

      if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
        idleId = window.requestIdleCallback(runDeferredUnread, { timeout: UNREAD_COUNT_DEFER_MS });
      } else {
        timeoutId = setTimeout(runDeferredUnread, UNREAD_COUNT_DEFER_MS);
      }
    }

    return () => {
      cancelled = true;
      if (idleId !== undefined && typeof window !== 'undefined' && 'cancelIdleCallback' in window) {
        window.cancelIdleCallback(idleId);
      }
      if (timeoutId !== undefined) clearTimeout(timeoutId);
      releaseNotificationHub();
    };
  }, [isAuthenticated, employee?.id, loadUnreadCount]);

  const markAsRead = useCallback(async (id: number) => {
    await notificationApi.markAsRead(id);
    setNotifications(prev =>
      prev.map(n => n.id === id ? { ...n, isRead: true, readAt: new Date().toISOString() } : n)
    );
    setUnreadCount(prev => Math.max(0, prev - 1));
  }, []);

  const markAsUnread = useCallback(async (id: number) => {
    await notificationApi.markAsUnread(id);
    let wasRead = false;
    setNotifications(prev => {
      const current = prev.find(n => n.id === id);
      wasRead = current?.isRead ?? false;
      return prev.map(n =>
        n.id === id ? { ...n, isRead: false, readAt: undefined } : n
      );
    });
    if (wasRead) setUnreadCount(c => c + 1);
  }, []);

  const markAllAsRead = useCallback(async () => {
    await notificationApi.markAllAsRead();
    setNotifications(prev => prev.map(n => ({ ...n, isRead: true, readAt: new Date().toISOString() })));
    setUnreadCount(0);
  }, []);

  const deleteNotification = useCallback(async (id: number) => {
    const notif = notifications.find(n => n.id === id);
    await notificationApi.delete(id);
    setNotifications(prev => prev.filter(n => n.id !== id));
    setTotalNotifications(prev => Math.max(0, prev - 1));
    if (notif && !notif.isRead) {
      setUnreadCount(prev => Math.max(0, prev - 1));
    }
  }, [notifications]);

  return (
    <NotificationContext.Provider value={{
      unreadCount,
      notifications,
      totalNotifications,
      loading,
      loadNotifications,
      markAsRead,
      markAsUnread,
      markAllAsRead,
      deleteNotification,
    }}>
      {children}
    </NotificationContext.Provider>
  );
};
